import axios from "axios";
import eventBus from "./eventBus";
import { isStandaloneDisplay, ensureServiceWorker, isPwaInstallAllowed } from "./pwaInstall";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function wantsPushNotifications(user) {
  if (!user) return false;
  if (user.pushNotifications === undefined || user.pushNotifications === null) return true;
  return Number(user.pushNotifications) !== 0;
}

export async function syncPushSubscription(user, { forceAsk = false } = {}) {
  try {
    if (!user || !isPushSupported()) return;
    if (!isPwaInstallAllowed(user)) return;
    if (!wantsPushNotifications(user)) return;

    if (Notification.permission === "granted") {
      await subscribeToPush();
      return;
    }
    if (Notification.permission === "denied") return;

    const canAutoAsk = forceAsk || isStandaloneDisplay();
    if (!canAutoAsk) return;

    if (Notification.permission === "default") {
      eventBus.$emit("pwa-ask-notifications");
      return;
    }
  } catch (err) {
    // Permission or subscription can fail on unsupported browsers.
  }
}

export async function enablePushFromUserGesture() {
  try {
    if (!isPushSupported()) return false;
    const result = await Notification.requestPermission();
    if (result !== "granted") return false;
    await subscribeToPush();
    return true;
  } catch (err) {
    return false;
  }
}

export async function setPushPreference(enabled) {
  await axios.post("/api/push/preference", { enabled: Boolean(enabled) });
  if (enabled) {
    await enablePushFromUserGesture();
  }
}

async function subscribeToPush() {
  await ensureServiceWorker();
  const registration = await navigator.serviceWorker.ready;
  if (!registration || !registration.pushManager) return;

  const { data } = await axios.get("/api/push/vapid-public-key");
  if (!data || !data.publicKey) return;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    }));

  await axios.post("/api/push/subscribe", subscription.toJSON());
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
